import type {HTMLAttributes, ReactNode} from 'react';

import {cn} from '../../helpers/cn';

export type BadgeVariant = 'count' | 'dot' | 'status';
export type BadgeColor = 'primary' | 'neutral' | 'inverse' | 'success' | 'error' | 'warning';
export type BadgeSize = 'sm' | 'md';

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  variant?: BadgeVariant;
  color?: BadgeColor;
  size?: BadgeSize;
  /** Used when variant is `count`. Numbers above maxCount render as `${maxCount}+`. */
  count?: number | string;
  maxCount?: number;
  children?: ReactNode;
};

const countSizeClasses: Record<BadgeSize, string> = {
  sm: 'min-h-4 min-w-4 px-0.5 text-[10px] leading-[17px]',
  md: 'size-5 text-[12px] leading-none'
};

const statusSizeClasses: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-[10px] leading-[17px]',
  md: 'px-2.5 py-1 text-[12px] leading-[15px]'
};

const dotSizeClasses: Record<BadgeSize, string> = {
  sm: 'size-1.5',
  md: 'size-2'
};

const colorClasses: Record<BadgeVariant, Record<BadgeColor, string>> = {
  count: {
    primary: 'bg-orange text-white',
    neutral: 'bg-gray-09 text-white',
    inverse: 'bg-white text-gray-09',
    success: 'bg-success text-success-on',
    error: 'bg-destructive text-destructive-on',
    warning: 'bg-yellow text-white'
  },
  dot: {
    primary: 'bg-orange',
    neutral: 'bg-gray-09',
    inverse: 'bg-white',
    success: 'bg-success',
    error: 'bg-destructive',
    warning: 'bg-yellow'
  },
  status: {
    primary: 'bg-orange-wash text-orange-deep',
    neutral: 'bg-gray-02 text-gray-08',
    inverse: 'bg-white text-gray-09',
    success: 'bg-success-surface text-success-heavy',
    error: 'bg-destructive-surface text-destructive-heavy',
    warning: 'bg-yellow-soft text-yellow-dark-03'
  }
};

function formatCount(count: number | string, maxCount: number): string {
  if (typeof count === 'string') return count;
  if (count > maxCount) return `${maxCount}+`;
  return String(count);
}

export function Badge({
  variant = 'status',
  color = 'primary',
  size = 'md',
  count,
  maxCount = 99,
  children,
  className,
  ...props
}: BadgeProps) {
  if (variant === 'dot') {
    return (
      <span
        {...props}
        aria-hidden
        className={cn(
          'inline-block shrink-0 rounded-full',
          dotSizeClasses[size],
          colorClasses.dot[color],
          className
        )}
      />
    );
  }

  if (variant === 'count') {
    const content = count == null ? null : formatCount(count, maxCount);

    if (content == null) return null;

    return (
      <span
        {...props}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full font-medium',
          countSizeClasses[size],
          colorClasses.count[color],
          className
        )}
      >
        {content}
      </span>
    );
  }

  if (children == null) return null;

  return (
    <span
      {...props}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-[4px] font-medium whitespace-nowrap',
        statusSizeClasses[size],
        colorClasses.status[color],
        className
      )}
    >
      {children}
    </span>
  );
}
